import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { ChevronDown } from "lucide-react";
import {
    getPOAuditLogRequest,
    PurchaseOrdersLoadingKeys,
} from "../../slices/purchasesOrdersSlice";

const FIELD_LABELS = {
    status: "Status",
    invoice: "Invoice #",
    total_value: "Total Value",
    shipping_cost: "Shipping Cost",
    packaging_cost: "Packaging Cost",
    notes: "Notes",
    po_date: "PO Date",
    quantity: "Ordered Quantity",
    quantity_received: "Received Quantity",
    price: "Unit Price",
    amount: "Payment Amount",
    type: "Payment Type",
    date_paid: "Date Paid",
    note: "Payment Note",
    transaction_id: "Transaction ID",
    box_qty: "Box Quantity",
    box_shipped: "Shipped Quantity",
    box_received: "Received Quantity",
    box_shipdate: "Ship Date",
    box_actual_date: "Received / Actual Date",
    container_qty: "Container Quantity",
    container_shipped: "Shipped Quantity",
    container_received: "Received Quantity",
};

const ACTION_LABELS = {
    INSERT: "Created",
    UPDATE: "Updated",
    DELETE: "Deleted",
};

const ACTION_TONES = {
    INSERT: "bg-green-100 text-green-700",
    UPDATE: "bg-blue-100 text-blue-700",
    DELETE: "bg-red-100 text-red-700",
};

function formatFieldLabel(field) {
    if (FIELD_LABELS[field]) return FIELD_LABELS[field];
    return String(field)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

const MONEY_FIELDS = new Set([
    "total_value",
    "shipping_cost",
    "packaging_cost",
    "price",
    "amount",
]);

function formatValue(value, field) {
    if (value === null || value === undefined || value === "") return "\u2014";
    if (typeof value === "object") return JSON.stringify(value);
    if (MONEY_FIELDS.has(field) && !Number.isNaN(Number(value))) {
        return `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return String(value);
}

function formatChangedAt(value) {
    if (!value) return "";
    try {
        return format(new Date(value), "MMM d, yyyy 'at' h:mm a");
    } catch {
        return "";
    }
}

export const POSidebarAuditTrail = ({ poId }) => {
    const dispatch = useDispatch();
    const { poAuditLog, loadingKeys } = useSelector((state) => state.purchasesOrders);
    const [expanded, setExpanded] = useState(false);

    const isLoading = loadingKeys.includes(PurchaseOrdersLoadingKeys.poAuditLog);

    useEffect(() => {
        setExpanded(false);
    }, [poId]);

    useEffect(() => {
        if (expanded && poId) {
            dispatch(getPOAuditLogRequest({ poId }));
        }
    }, [expanded, poId, dispatch]);

    const entries = poAuditLog || [];

    return (
        <div className="mt-6">
            <h3 className="text-base font-bold mb-2">Change Log</h3>
            <button
                type="button"
                onClick={() => setExpanded((current) => !current)}
                className="flex items-center gap-1.5 mb-2 text-[13px] font-semibold text-gray-700 hover:text-gray-900 transition-colors"
            >
                <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-150 ${expanded ? "" : "-rotate-90"}`}
                />
                {expanded ? "Hide history" : "Show history"}
            </button>
            {expanded && (
                <div className="space-y-2">
                    <div className="space-y-2 overflow-y-auto max-h-[400px]">
                        {isLoading ? (
                            <p className="text-[12px] text-gray-500 italic py-3 text-center">
                                Loading change history...
                            </p>
                        ) : entries.length === 0 ? (
                            <p className="text-[12px] text-gray-500 italic py-3 text-center">
                                No changes recorded yet.
                            </p>
                        ) : (
                            entries.map((entry) => (
                                <div
                                    key={entry.id}
                                    className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${ACTION_TONES[entry.action] || "bg-gray-100 text-gray-700"}`}>
                                                {ACTION_LABELS[entry.action] || entry.action}
                                            </span>
                                            <span className="text-[11px] font-semibold text-gray-900 truncate">
                                                {entry.entityLabel}
                                                {entry.context ? ` \u00b7 ${entry.context}` : ""}
                                            </span>
                                        </div>
                                        <span className="text-[10px] text-gray-500 shrink-0">
                                            {formatChangedAt(entry.createdAt)}
                                        </span>
                                    </div>
                                    <div className="mt-0.5 text-[11px] text-gray-500">
                                        {entry.userName || (entry.changeSource === "MIGRATION" ? "System sync" : "Unknown user")}
                                    </div>
                                    {(entry.changes || []).length > 0 && (
                                        <div className="mt-1 space-y-0.5">
                                            {entry.changes.map((change, index) => (
                                                <div key={`${entry.id}_${change.field}_${index}`} className="text-[12px] text-gray-900">
                                                    <span className="font-medium">{formatFieldLabel(change.field)}:</span>{" "}
                                                    <span className="text-gray-500 line-through">{formatValue(change.oldValue, change.field)}</span>
                                                    {" \u2192 "}
                                                    <span>{formatValue(change.newValue, change.field)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
